import { getFirstDisplayText, getPureArray } from "./util";

const CURRENCY = "HKD";

const SENSITIVE_QUERY_KEYS = ["email", "password", "token", "phone", "account"];

export type AnalyticsItem = {
    item_id: string;
    item_name: string;
    price?: number;
    quantity?: number;
    item_variant?: string;
    index?: number;
    item_list_id?: string;
    item_list_name?: string;
};

type EcommerceExtra = {
    value?: number;
    coupon?: string;
    transaction_id?: string;
    shipping?: number;
    payment_type?: string;
    shipping_tier?: string;
    item_list_id?: string;
    item_list_name?: string;
};

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
        gtag?: (...args: any[]) => void;
    }
}

let lastPagePath = "";

const toNumber = (value: unknown) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const sendEvent = (event: string, params: Record<string, unknown> = {}, clearEcommerce = false) => {
    window.dataLayer = window.dataLayer || [];
    if (clearEcommerce) {
        window.dataLayer.push({ ecommerce: null });
    }
    // 保留 dataLayer，供 GTM 容器使用
    window.dataLayer.push({ event, ...params });
    // 直接写入 GA4 评估 ID，确保无需依赖 GTM 标签即可开始收集
    if (typeof window.gtag === "function") {
        window.gtag("event", event, params);
    }
};

const sanitizeSearch = (search: string) => {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    SENSITIVE_QUERY_KEYS.forEach((key) => params.delete(key));
    const next = params.toString();
    return next ? `?${next}` : "";
};

const resolvePageTitle = (pathname: string) => {
    const [section, id] = pathname.split("/").filter(Boolean);
    if (!section) {
        return "home";
    }
    if (section === "product-detail") {
        return id ? `product_detail_${id}` : "product_detail";
    }
    if (section === "category") {
        return id ? `category_${id}` : "category";
    }
    return section;
};

const normalizeItems = (items: AnalyticsItem[]) => {
    return items
        .filter((item) => item.item_id && item.item_name)
        .map((item) => {
            const price = toNumber(item.price);
            const quantity = toNumber(item.quantity);
            return {
                item_id: String(item.item_id),
                item_name: String(item.item_name),
                price,
                quantity: quantity > 0 ? quantity : 1,
                ...(item.item_variant ? { item_variant: item.item_variant } : {}),
                ...(item.index != null ? { index: item.index } : {}),
                ...(item.item_list_id ? { item_list_id: item.item_list_id } : {}),
                ...(item.item_list_name ? { item_list_name: item.item_list_name } : {}),
            };
        });
};

const itemsValue = (items: ReturnType<typeof normalizeItems>) => {
    return Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2));
};

export const cartToItems = (cartSkuList: any[]): AnalyticsItem[] => {
    const items: AnalyticsItem[] = [];
    getPureArray<any>(cartSkuList).forEach((product) => {
        const productName = getFirstDisplayText(product?.productName, product?.productNameHk, product?.productNameEn, product?.title, product?.name);
        getPureArray<any>(product?.skuList).forEach((sku) => {
            const quantity = toNumber(sku?.quantity);
            if (quantity <= 0) {
                return;
            }
            const skuName = getFirstDisplayText(sku?.name, sku?.nameHk, sku?.nameEn);
            items.push({
                item_id: String(sku?.id || product?.productId || ""),
                item_name: productName || skuName || String(product?.productId || sku?.id || ""),
                item_variant: skuName || undefined,
                price: toNumber(sku?.sellingPrice ?? sku?.price),
                quantity,
            });
        });
    });
    return items;
};

export const trackEcommerce = (event: string, items: AnalyticsItem[], extra: EcommerceExtra = {}) => {
    const normalized = normalizeItems(items);
    if (!normalized.length && !extra.transaction_id) {
        return;
    }
    const value = extra.value != null && Number.isFinite(Number(extra.value)) ? Number(extra.value) : itemsValue(normalized);
    const params: Record<string, unknown> = {
        currency: CURRENCY,
        value,
        items: normalized,
    };
    if (extra.coupon) {
        params.coupon = extra.coupon;
    }
    if (extra.transaction_id) {
        params.transaction_id = extra.transaction_id;
    }
    if (extra.shipping != null) {
        params.shipping = toNumber(extra.shipping);
    }
    if (extra.payment_type) {
        params.payment_type = extra.payment_type;
    }
    if (extra.shipping_tier) {
        params.shipping_tier = extra.shipping_tier;
    }
    if (extra.item_list_id) {
        params.item_list_id = extra.item_list_id;
    }
    if (extra.item_list_name) {
        params.item_list_name = extra.item_list_name;
    }
    sendEvent(event, params, true);
};

export const trackPageView = (pathname: string, search = "") => {
    const pagePath = `${pathname}${sanitizeSearch(search)}`;
    if (!pagePath || pagePath === lastPagePath) {
        return;
    }
    lastPagePath = pagePath;
    sendEvent("page_view", {
        page_path: pagePath,
        page_location: `${window.location.origin}${pagePath}`,
        page_title: resolvePageTitle(pathname),
    });
};

export const trackViewItemList = (items: AnalyticsItem[], list: { item_list_id?: string; item_list_name?: string } = {}) => {
    trackEcommerce("view_item_list", items.map((item) => ({ ...item, ...list })), list);
};

export const trackSelectItem = (item: AnalyticsItem) => {
    trackEcommerce("select_item", [item], {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
    });
};

export const trackViewItem = (item: AnalyticsItem) => {
    trackEcommerce("view_item", [item]);
};

export const trackAddToCart = (items: AnalyticsItem[]) => {
    trackEcommerce("add_to_cart", items);
};

export const trackRemoveFromCart = (items: AnalyticsItem[]) => {
    trackEcommerce("remove_from_cart", items);
};

export const trackViewCart = (items: AnalyticsItem[], extra: EcommerceExtra = {}) => {
    trackEcommerce("view_cart", items, extra);
};

export const trackBeginCheckout = (items: AnalyticsItem[], extra: EcommerceExtra = {}) => {
    trackEcommerce("begin_checkout", items, extra);
};

export const trackAddShippingInfo = (items: AnalyticsItem[], extra: EcommerceExtra = {}) => {
    trackEcommerce("add_shipping_info", items, extra);
};

export const trackAddPaymentInfo = (items: AnalyticsItem[], extra: EcommerceExtra = {}) => {
    trackEcommerce("add_payment_info", items, extra);
};

export const trackPurchase = (params: EcommerceExtra & { transactionId: string; items: AnalyticsItem[] }) => {
    if (!params.transactionId) {
        return;
    }
    const storageKey = `analytics_purchase_${params.transactionId}`;
    try {
        if (window.sessionStorage.getItem(storageKey)) {
            return;
        }
        window.sessionStorage.setItem(storageKey, "1");
    } catch (error) {
        // sessionStorage 不可用时仍上报，避免漏记成交
    }
    trackEcommerce("purchase", params.items, {
        ...params,
        transaction_id: params.transactionId,
    });
};

export const trackLogin = (method: string) => {
    sendEvent("login", { method });
};

export const trackSignUp = (method: string) => {
    sendEvent("sign_up", { method });
};

export const trackGenerateLead = () => {
    sendEvent("generate_lead");
};
